"use client";

import { PropertyOverviewBlock } from "@/payload-types";
import { Funnel, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useCallback, useState, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import PropertyOverviewFiltersForm from "@/components/frontend/marketsuite/property/filters";
import Card, { CardContent, CardFooter, CardHeader } from "@/components/frontend/ui/card";
import { Button } from "@/components/frontend/ui/button";
import { Drawer } from "@/components/frontend/ui/drawer";
import { cn } from "@/lib/utils";
import { MsRoutes } from "@/lib/types/marketsuite/property-route";
import { trackPropertyViewItemList } from "@/lib/data-measurements/property-ecommerce";
import { PropertyOverviewResults, type PropertyOverviewResultsData } from "./results";
import { PropertyListingType } from "@/lib/types/marketsuite/datatypes/property";
import { initialFilterState, PropertyOverviewFilters, usePropertyFilterState } from "./filter-state";

function normalizeListingPathname(pathname: string | null): string {
	if (!pathname || pathname.length === 0) return "property-listing";
	const withoutPage = pathname.replace(/\/page\/\d+$/, "");
	return withoutPage === "" ? "/" : withoutPage;
}

type Settings = NonNullable<PropertyOverviewBlock["settings"]>;
interface PropertyOverviewDataProps {
	locale: string;
	basePath: string;
	children: ReactNode;
	filters: PropertyOverviewFilters;
	initialResult: PropertyOverviewResultsData;
	columns: Settings["columns"];
	paginationButtonVariant: Settings["pagBtn"];
	propertyCardVariant: Settings["propCard"];
	filterCardVariant: Settings["filterCard"];
	filterFormVariant: Settings["filterFormVariant"];
	sortFormVariant: Settings["sortFormVariant"];
	listingType?: PropertyListingType;
	partnerId?: string;
	msRoutes?: MsRoutes;
	stickyFilters?: boolean | null;
	randomSort?: boolean;
	defaultSort?: Settings["defaultSort"];
}

function getCurrentSearchParams(): URLSearchParams {
	if (typeof window === "undefined") return new URLSearchParams();

	return new URLSearchParams(window.location.search);
}

const PropertyOverviewData: React.FC<PropertyOverviewDataProps> = ({
	locale,
	basePath,
	children,
	filters,
	initialResult,
	columns,
	paginationButtonVariant,
	propertyCardVariant,
	filterCardVariant,
	filterFormVariant,
	sortFormVariant,
	listingType,
	partnerId,
	msRoutes = {},
	stickyFilters = true,
	randomSort = false,
	defaultSort,
}) => {
	const t = useTranslations("ms");
	const pathname = usePathname();
	const listingPathname = useMemo(() => normalizeListingPathname(pathname), [pathname]);
	const [isClientControlled, setIsClientControlled] = useState(false);
	const [formResetCount, setFormResetCount] = useState(0);

	const { setSelectedFilters, filterState, setFilterState, hasAppliedFilters, defaultFilters, filterQuery } = usePropertyFilterState({
		filters,
		onFilterChange: () => setIsClientControlled(true),
		listingType,
		partnerId,
		defaultSort,
		basePath,
		disableUrlSync: !isClientControlled,
	});
	const trackedFilterKey = useMemo(() => JSON.stringify(filterState.filters ?? {}), [filterState.filters]);
	const lastTrackedRef = useRef<{ filterKey: string; page: number } | null>(null);

	useEffect(() => {
		const nextFilterState = initialFilterState(getCurrentSearchParams(), filters, defaultFilters, listingType, partnerId, defaultSort);
		setFilterState(nextFilterState);
		setIsClientControlled(nextFilterState.hasClientParams);

		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [defaultFilters, filters, listingType, partnerId, defaultSort]);

	const { data, isFetching } = useQuery({
		queryKey: ["properties", filterState.filters, filterState.page, filterState.sort, randomSort],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.set("page", filterState.page.toString());
			if (filterState.sort) params.set("sort", filterState.sort);
			if (randomSort) params.set("random-sort", "true");

			for (const [key, value] of Object.entries(filterState.filters)) {
				if (Array.isArray(value)) {
					for (const item of value) params.append(key, item.toString());
				} else if (value) {
					params.set(key, value.toString());
				}
			}

			const response = await fetch(`/${locale}/next/property-overview?${params.toString()}`);
			if (!response.ok) throw new Error("Failed to load properties.");

			return response.json() as Promise<PropertyOverviewResultsData>;
		},
		enabled: isClientControlled,
		initialData: isClientControlled ? undefined : initialResult,
		placeholderData: (previousData) => previousData ?? initialResult,
	});

	const lastVisibleDataRef = useRef(initialResult);
	if (data?.data.length) lastVisibleDataRef.current = data;
	const visibleData = isFetching && !data?.data.length ? lastVisibleDataRef.current : (data ?? lastVisibleDataRef.current);

	useEffect(() => {
		if (!isClientControlled) return;
		if (!data?.data?.length) return;
		if (data.meta?.current_page !== filterState.page) return;

		if (!lastTrackedRef.current) {
			trackPropertyViewItemList(data.data, listingPathname, listingPathname);
			lastTrackedRef.current = { filterKey: trackedFilterKey, page: filterState.page };
			return;
		}

		if (lastTrackedRef.current.filterKey !== trackedFilterKey) {
			lastTrackedRef.current = { filterKey: trackedFilterKey, page: filterState.page };
			return;
		}

		if (lastTrackedRef.current.page !== filterState.page) {
			trackPropertyViewItemList(data.data, listingPathname, listingPathname);
			lastTrackedRef.current = { filterKey: trackedFilterKey, page: filterState.page };
		}
	}, [data, isClientControlled, listingPathname, trackedFilterKey, filterState.page]);

	const resetFilters = useCallback(() => {
		setSelectedFilters(initialFilterState(new URLSearchParams(), filters, defaultFilters, listingType, partnerId).filters);
		setFormResetCount((count) => count + 1);
	}, [defaultFilters, filters, listingType, partnerId, setSelectedFilters]);

	const filtersForm = (
		// The key remounts the form on reset so field state (e.g. slider positions) returns to the defaults.
		<PropertyOverviewFiltersForm
			key={formResetCount}
			filters={filters}
			selectedFilters={filterState.filters}
			formVariant={filterFormVariant ?? undefined}
			setSelectedFilters={setSelectedFilters}
		/>
	);

	return (
		<>
			<div className="flex gap-4">
				<Card
					className={cn(
						"hidden lg:flex h-max w-full max-w-90 flex-col transition-all",
						stickyFilters && "sticky top-[calc(var(--header-height)+(--spacing(8)))] max-h-page",
					)}
					variant={filterCardVariant}
				>
					{filters?.title && (
						<CardHeader>
							<p className="h5">{filters.title}</p>
						</CardHeader>
					)}

					<CardContent className={cn(stickyFilters ? "min-h-0 flex-1 overflow-auto h-max" : "h-max")}>{filtersForm}</CardContent>

					{hasAppliedFilters && (
						<CardFooter>
							<Button className="w-full justify-center" variant="subtle" type="button" onClick={resetFilters}>
								<X className="size-4" />
								{t("property-overview.remove-filters")}
							</Button>
						</CardFooter>
					)}
				</Card>

				{!isClientControlled && children}

				{isClientControlled && (
					<PropertyOverviewResults
						basePath={basePath}
						columns={columns}
						data={visibleData}
						filterQuery={filterQuery}
						itemListId={listingPathname}
						itemListName={listingPathname}
						msRoutes={msRoutes}
						paginationButtonVariant={paginationButtonVariant}
						propertyCardVariant={propertyCardVariant}
						pending={isFetching}
						sort={filterState.sort}
						sortFormVariant={sortFormVariant ?? undefined}
						onPageChange={(page) => {
							setIsClientControlled(true);
							setFilterState((prev) => ({ ...prev, page, hasClientParams: true }));
						}}
						onSortChange={(sort) => {
							setIsClientControlled(true);
							setFilterState((prev) => ({ ...prev, sort, page: 1, hasClientParams: true }));
						}}
					/>
				)}
			</div>

			<Drawer
				variant={filterCardVariant}
				trigger={
					<Button type="button" className="z-100000 fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-1 lg:hidden">
						<Funnel className="size-4" /> {t("property-overview.filters")}
					</Button>
				}
				close={
					<div className="flex flex-col justify-center w-full gap-2">
						<Button>
							<Funnel className="size-4" /> {t("property-overview.filter")}
						</Button>

						{hasAppliedFilters && (
							<Button variant="subtle" type="button" onClick={resetFilters}>
								<X className="size-4" />
								{t("property-overview.remove-filters")}
							</Button>
						)}
					</div>
				}
			>
				{filtersForm}
			</Drawer>
		</>
	);
};

export default PropertyOverviewData;
